import { defineStore } from 'pinia';
import { useRoute } from 'vue-router';
import { iframeCommunication } from '@/utils/iframeCommunication';

function normalizeCredential(value: string | null | undefined): string | null {
  if (value == null) return null;
  const trimmed = String(value).trim();
  return trimmed.length > 0 ? trimmed : null;
}

/** Hosts often pass "-" when no player JWT; never treat as logged-in or send as auth-token. */
export function normalizePlayerToken(value: string | null | undefined): string | null {
  const n = normalizeCredential(value);
  if (!n) return null;
  if (n === '-') return null;
  return n;
}

function readAuthParamsFromWindow(): { token: string | null; account: string | null } {
  try {
    const params = new URLSearchParams(window.location.search);
    return {
      token: normalizePlayerToken(params.get('token')),
      account: normalizeCredential(params.get('account')),
    };
  } catch {
    return { token: null, account: null };
  }
}

export interface AuthState {
  token: string | null;
  account: string | null;
  loading: boolean;
  error: string | null;
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    token: null,
    account: null,
    loading: false,
    error: null,
  }),

  getters: {
    isAuthenticated: (state) =>
      !!(
        normalizePlayerToken(state.token) &&
        normalizeCredential(state.account)
      ),
    getAuthHeaders: (state) => {
      const token = normalizePlayerToken(state.token);
      const account = normalizeCredential(state.account);
      if (!token || !account) {
        return {};
      }
      return {
        'auth-token': token,
        'auth-account': account,
      };
    },
  },

  actions: {
    async authenticate() {
      this.loading = true;
      this.error = null;

      try {
        const route = useRoute();
        const token = route.query.token as string;
        const account = route.query.account as string;
        
        // URL query params are the source of truth
        const normToken = normalizePlayerToken(token);
        const normAccount = normalizeCredential(account);
        if (!normToken || !normAccount) {
          sessionStorage.removeItem('auth_token');
          sessionStorage.removeItem('auth_account');
          this.token = null;
          this.account = null;
          this.loading = false;
          return false;
        }

        // Set the token and account from query parameters
        this.token = normToken;
        this.account = normAccount;
        
        // Store in sessionStorage for session persistence
        sessionStorage.setItem('auth_token', normToken);
        sessionStorage.setItem('auth_account', normAccount);
        
        return true;
        
      } catch (e: unknown) {
        const error = e as { userMessage?: string; message?: string };
        this.error = error?.userMessage || error?.message || 'Authentication failed';
        this.loading = false;
        return false;
      } finally {
        this.loading = false;
      }
    },

    initializeFromSession() {
      const token = normalizePlayerToken(sessionStorage.getItem('auth_token'));
      const account = normalizeCredential(sessionStorage.getItem('auth_account'));

      if (!token) {
        sessionStorage.removeItem('auth_token');
        this.token = null;
      } else {
        sessionStorage.setItem('auth_token', token);
        this.token = token;
      }

      if (!account) {
        sessionStorage.removeItem('auth_account');
        this.account = null;
      } else {
        sessionStorage.setItem('auth_account', account);
        this.account = account;
      }
    },

    /**
     * Align Pinia auth with URL + session before catalog/cutoff API calls.
     */
    refreshAuthFromExternalSources() {
      const { token: urlToken, account: urlAccount } = readAuthParamsFromWindow();
      const sessionToken = normalizePlayerToken(sessionStorage.getItem('auth_token'));
      const sessionAccount = normalizeCredential(sessionStorage.getItem('auth_account'));

      if (urlToken) {
        sessionStorage.setItem('auth_token', urlToken);
        this.token = urlToken;
      } else if (sessionToken && !this.token) {
        this.token = sessionToken;
      }

      if (urlAccount) {
        sessionStorage.setItem('auth_account', urlAccount);
        this.account = urlAccount;
      } else if (sessionAccount && !this.account) {
        this.account = sessionAccount;
      }
    },

    /** Tenant slug for catalog/cutoff APIs: URL → session → Pinia (after refresh). */
    resolveTenantAccount(): string | null {
      this.refreshAuthFromExternalSources();
      return (
        this.account ||
        normalizeCredential(
          typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('auth_account') : null
        ) ||
        readAuthParamsFromWindow().account
      );
    },

    /**
     * Apply iframe parent-ready config synchronously before catalog API calls run.
     * Clears only the user token when absent — never wipes tenant account.
     */
    applyParentConfig(config: Record<string, unknown>) {
      const account = typeof config.account === 'string' ? config.account.trim() : '';
      if (account) {
        this.account = account;
        sessionStorage.setItem('auth_account', account);
      }

      const currency = typeof config.currency === 'string' ? config.currency.trim() : '';
      if (currency) {
        sessionStorage.setItem('auth_currency', currency);
      }

      const token = normalizePlayerToken(
        typeof config.token === 'string' ? config.token : config.token != null ? String(config.token) : null
      );
      if (token) {
        this.token = token;
        sessionStorage.setItem('auth_token', token);
      } else {
        this.clearInvalidTokenKeepAccount();
      }
    },

    /**
     * Drop only the user token; keep account (tenant) so API/game context stays valid.
     * Does not notify the parent — avoids iframe reload on wagers when the user simply
     * needs to log in (hosts often refresh the whole iframe on session-invalid).
     */
    clearInvalidTokenKeepAccount() {
      this.token = null;
      sessionStorage.removeItem('auth_token');
    },

    // Method to clear invalid credentials when 401 error occurs
    clearInvalidCredentials() {
      this.token = null;
      sessionStorage.removeItem('auth_token');
      // Keep auth_account — tenant context (cutoffs, theme, catalog) is independent of user token.

      iframeCommunication.sendMessage('session-invalid', {
        reason: 'token-expired-or-invalid'
      });
    },

    logout(options?: { silent?: boolean; reason?: string }) {
      this.token = null;
      this.account = null;
      sessionStorage.removeItem('auth_token');
      sessionStorage.removeItem('auth_account');
      if (!options?.silent) {
        iframeCommunication.sendMessage('logout', {
          reason: options?.reason ?? 'user-initiated'
        });
      }
    },

    // Method to check if we have valid session credentials
    hasValidSession() {
      const token = normalizePlayerToken(sessionStorage.getItem('auth_token'));
      const account = normalizeCredential(sessionStorage.getItem('auth_account'));
      return !!(token && account);
    },

    // Method to force re-initialization from session storage
    reinitializeFromSession() {
      this.initializeFromSession();
    },
  },
});